import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { FileText, FileSpreadsheet, FileArchive, Lock, Download, ShieldCheck, LogOut, Mail, KeyRound, ArrowLeft, Table, Send, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAVY = "#0A0A46";
const LIME = "#8FB000";
const CARD_ICONS = {
  "Relazioni PDF": FileText,
  "Conversazioni scritte": FileSpreadsheet,
  "Chiamate telefoniche": FileArchive,
};

function iconFor(item) {
  if (item.filename.endsWith(".pdf")) return FileText;
  if (item.filename.endsWith(".xlsx")) return FileSpreadsheet;
  if (item.filename.endsWith(".zip")) return FileArchive;
  return FileText;
}

const DONUT_COLORS = ["#8FB000", "#0A0A46", "#26379A", "#A6CC1A", "#E8A33D", "#2E9E6B", "#D64545"];

function Donut({ data }) {
  const total = data.reduce((s, d) => s + d.count, 0) || 1;
  const R = 52, SW = 20, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <svg width="132" height="132" viewBox="0 0 132 132">
      <g transform="rotate(-90 66 66)">
        {data.map((d, i) => {
          const len = (d.count / total) * C;
          const seg = (
            <circle key={d.name} cx="66" cy="66" r={R} fill="none"
              stroke={DONUT_COLORS[i % DONUT_COLORS.length]} strokeWidth={SW}
              strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} />
          );
          offset += len;
          return seg;
        })}
      </g>
      <text x="66" y="62" textAnchor="middle" fontSize="20" fontWeight="800" fill="#0A0A46">{total}</text>
      <text x="66" y="80" textAnchor="middle" fontSize="9" fill="#5B647B">chiamate</text>
    </svg>
  );
}

export default function App() {
  const [token, setToken] = useState(sessionStorage.getItem("portal_token") || "");
  const [step, setStep] = useState("code"); // code | email | otp
  const [codeInput, setCodeInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [sentEmail, setSentEmail] = useState("");
  const [artifacts, setArtifacts] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [config, setConfig] = useState({ expired: false, expiry: null });
  const [stats, setStats] = useState(null);
  const [previews, setPreviews] = useState({});
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteMsg, setInviteMsg] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/portal/config`).then((r) => {
      setConfig(r.data);
      if (r.data.default_invite) setInviteEmails(r.data.default_invite);
    }).catch(() => {});
  }, []);

  const sendInvite = async () => {
    const recipients = inviteEmails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) { setInviteMsg({ ok: false, text: "Inserisci almeno un indirizzo email." }); return; }
    setInviteLoading(true); setInviteMsg(null);
    try {
      const r = await axios.post(`${API}/portal/send-invite`, { recipients, portal_url: window.location.origin }, { params: { token } });
      setInviteMsg({ ok: true, text: `Istruzioni inviate a ${r.data.count} destinatario${r.data.count > 1 ? "i" : ""}: ${r.data.sent.join(", ")}` });
    } catch (e) {
      setInviteMsg({ ok: false, text: e?.response?.data?.detail || "Invio non riuscito. Riprova." });
    } finally { setInviteLoading(false); }
  };

  const loadStats = useCallback(async (t) => {
    try {
      const r = await axios.get(`${API}/portal/stats`, { params: { token: t } });
      setStats(r.data && Object.keys(r.data).length ? r.data : null);
    } catch { setStats(null); }
  }, []);

  const fetchXlsx = async (key, sheet) => {
    try {
      const r = await axios.get(`${API}/portal/xlsx-preview/${key}`, { params: { token, sheet } });
      setPreviews((p) => ({ ...p, [key]: r.data }));
    } catch { /* noop */ }
  };

  const toggleXlsx = (key) => {
    if (previews[key]) { setPreviews((p) => ({ ...p, [key]: null })); return; }
    fetchXlsx(key);
  };

  const load = useCallback(async (t) => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/portal/artifacts`, { params: { token: t } });
      setArtifacts(res.data.artifacts || []);
      setAuthed(true);
      sessionStorage.setItem("portal_token", t);
      setToken(t);
      loadStats(t);
    } catch (e) {
      setAuthed(false);
      sessionStorage.removeItem("portal_token");
      setToken("");
    } finally { setLoading(false); }
  }, [loadStats]);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const submitCode = () => {
    if (!codeInput.trim()) return;
    setError(""); setStep("email");
  };

  const requestOtp = async () => {
    setLoading(true); setError(""); setInfo("");
    try {
      const res = await axios.post(`${API}/portal/auth/request-otp`, { code: codeInput.trim(), email: emailInput.trim() });
      setSentEmail(res.data.email);
      setStep("otp");
      setInfo(`Codice inviato a ${res.data.email}. Controlla la posta (anche spam).`);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401) { setError("Codice di accesso non valido."); setStep("code"); }
      else if (st === 403) setError("Questo indirizzo email non è autorizzato ad accedere al portale.");
      else setError(e?.response?.data?.detail || "Errore invio codice. Riprova.");
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/portal/auth/verify-otp`, { email: sentEmail, otp: otpInput.trim() });
      await load(res.data.token);
    } catch (e) {
      setError(e?.response?.data?.detail || "Codice non valido.");
    } finally { setLoading(false); }
  };

  const logout = () => {
    sessionStorage.removeItem("portal_token");
    setToken(""); setAuthed(false); setArtifacts([]);
    setStep("code"); setCodeInput(""); setEmailInput(""); setOtpInput(""); setSentEmail(""); setError(""); setInfo("");
  };

  // ---- Portale scaduto ----
  if (config.expired) {
    return (
      <div style={styles.loginWrap}>
        <div style={styles.loginOverlay} />
        <div style={styles.loginCard} data-testid="portal-expired-card">
          <img src="/telcavoip_logo.png" alt="TELCAVOIP" style={{ width: 190, marginBottom: 6 }} />
          <div style={styles.intl}>I N T E R N A T I O N A L</div>
          <div style={styles.limeBar} />
          <h1 style={styles.loginTitle}>Accesso non più disponibile</h1>
          <p style={styles.loginSub}>Il portale download riservato è stato disattivato in data <b>{config.expiry}</b>. Per riottenere l'accesso ai deliverable, contatta TELCAVOIP INTERNATIONAL.</p>
          <div style={styles.confidential}><ShieldCheck size={14} /> Contenuti riservati e confidenziali</div>
        </div>
        <div style={styles.footer}>TELCAVOIP INTERNATIONAL · documento riservato al management di SOSTRAVEL.COM</div>
      </div>
    );
  }

  // ---- Login / OTP screens ----
  if (!authed) {
    return (
      <div style={styles.loginWrap}>
        <div style={styles.loginOverlay} />
        <div style={styles.loginCard} data-testid="portal-login-card">
          <img src="/telcavoip_logo.png" alt="TELCAVOIP" style={{ width: 190, marginBottom: 6 }} />
          <div style={styles.intl}>I N T E R N A T I O N A L</div>
          <div style={styles.limeBar} />
          <h1 style={styles.loginTitle}>Portale download riservato</h1>

          {step === "code" && (
            <>
              <p style={styles.loginSub}>Area protetta per <b>SOSTRAVEL.COM</b>. Inserisci il codice di accesso fornito da TELCAVOIP.</p>
              <div style={styles.inputRow}>
                <Lock size={18} color={NAVY} />
                <input
                  data-testid="portal-code-input"
                  type="password"
                  placeholder="Codice di accesso"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitCode()}
                  style={styles.input}
                />
              </div>
              {error && <div data-testid="portal-error" style={styles.error}>{error}</div>}
              <button data-testid="portal-code-btn" style={styles.primaryBtn} disabled={!codeInput.trim()} onClick={submitCode}>
                Continua
              </button>
              <div style={styles.stepDots}><span style={styles.dotActive} /><span style={styles.dot} /><span style={styles.dot} /></div>
            </>
          )}

          {step === "email" && (
            <>
              <p style={styles.loginSub}>Inserisci la tua email aziendale autorizzata. Ti invieremo un <b>codice monouso</b> per completare l'accesso.</p>
              <div style={styles.inputRow}>
                <Mail size={18} color={NAVY} />
                <input
                  data-testid="portal-email-input"
                  type="email"
                  placeholder="nome@telcavoip.ai · @telcavoip.eu · @sostravel.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                  style={styles.input}
                />
              </div>
              {error && <div data-testid="portal-error" style={styles.error}>{error}</div>}
              <button data-testid="portal-email-btn" style={styles.primaryBtn} disabled={loading || !emailInput.trim()} onClick={requestOtp}>
                {loading ? "Invio…" : "Invia codice via email"}
              </button>
              <button data-testid="portal-back-btn" style={styles.linkBtn} onClick={() => { setStep("code"); setError(""); }}>
                <ArrowLeft size={13} /> Indietro
              </button>
              <div style={styles.stepDots}><span style={styles.dotDone} /><span style={styles.dotActive} /><span style={styles.dot} /></div>
            </>
          )}

          {step === "otp" && (
            <>
              <p style={styles.loginSub}>Abbiamo inviato un codice a <b>{sentEmail}</b>. Inseriscilo qui sotto per accedere.</p>
              {info && <div data-testid="portal-info" style={styles.infoBox}>{info}</div>}
              <div style={styles.inputRow}>
                <KeyRound size={18} color={NAVY} />
                <input
                  data-testid="portal-otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Codice a 6 cifre"
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && verifyOtp()}
                  style={{ ...styles.input, letterSpacing: 4, fontWeight: 700 }}
                />
              </div>
              {error && <div data-testid="portal-error" style={styles.error}>{error}</div>}
              <button data-testid="portal-otp-btn" style={styles.primaryBtn} disabled={loading || otpInput.length < 6} onClick={verifyOtp}>
                {loading ? "Verifica…" : "Accedi al portale"}
              </button>
              <button data-testid="portal-resend-btn" style={styles.linkBtn} onClick={requestOtp} disabled={loading}>
                Reinvia codice
              </button>
              <div style={styles.stepDots}><span style={styles.dotDone} /><span style={styles.dotDone} /><span style={styles.dotActive} /></div>
            </>
          )}

          <div style={styles.confidential}><ShieldCheck size={14} /> Accesso a due fattori · contenuti riservati</div>
          {config.expiry && <div style={styles.expiryNote}>Accesso valido fino al {config.expiry}</div>}
        </div>
        <div style={styles.footer}>TELCAVOIP INTERNATIONAL · documento riservato al management di SOSTRAVEL.COM</div>
      </div>
    );
  }

  // ---- Portal ----
  const categories = [...new Set(artifacts.map((a) => a.category))];
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src="/telcavoip_logo_white.webp" alt="TELCAVOIP" style={{ height: 30 }} />
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: 0.5 }}>Portale Download</div>
              <div style={{ color: "#9fb0d0", fontSize: 11 }}>Progetto SOSTRAVEL.COM</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button data-testid="portal-invite-btn" style={styles.inviteBtn} onClick={() => { setShowInvite(true); setInviteMsg(null); }}><Send size={14} /> Invia istruzioni</button>
            <button data-testid="portal-logout-btn" style={styles.logoutBtn} onClick={logout}><LogOut size={14} /> Esci</button>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        {config.warn && (
          <div style={styles.warnBanner} data-testid="portal-expiry-banner">
            <span style={styles.warnDot} />
            L'accesso a questo portale scadrà il <b>&nbsp;{config.expiry}</b>
            {typeof config.days_left === "number" && <>&nbsp;· tra {config.days_left} {config.days_left === 1 ? "giorno" : "giorni"}</>}. Scarica i file di cui hai bisogno per tempo.
          </div>
        )}
        <div style={styles.limeBar} />
        <h1 style={styles.h1}>Dati e relazioni disponibili</h1>
        <p style={styles.lead}>Scarica i deliverable dell'analisi e dell'estrazione dati del customer care. I file sono aggiornati automaticamente al termine di ogni elaborazione.</p>

        {stats && (
          <div style={styles.dashWrap} data-testid="portal-dashboard">
            <div style={styles.statGrid}>
              <div style={styles.statCard} data-testid="stat-calls">
                <div style={styles.statValue}>{stats.calls_total.toLocaleString("it-IT")}</div>
                <div style={styles.statLabel}>Chiamate analizzate</div>
                <div style={styles.statSub}>{stats.calls_answer_rate}% risposte</div>
              </div>
              <div style={styles.statCard} data-testid="stat-tickets">
                <div style={styles.statValue}>{stats.tickets_total.toLocaleString("it-IT")}</div>
                <div style={styles.statLabel}>Ticket analizzati</div>
                <div style={styles.statSub}>{stats.tickets_resolved_pct}% risolti · mediana {stats.tickets_res_median_h}h</div>
              </div>
              <div style={styles.statCard} data-testid="stat-sample">
                <div style={styles.statValue}>{stats.sample_calls_transcribed.toLocaleString("it-IT")}</div>
                <div style={styles.statLabel}>Chiamate trascritte + AI</div>
                <div style={styles.statSub}>campione biennio</div>
              </div>
            </div>
            {stats.top_clusters && stats.top_clusters.length > 0 && (
              <div style={styles.clusterBox}>
                <div style={styles.clusterTitle}>Motivi principali delle chiamate (cluster AI)</div>
                <div style={styles.clusterLayout}>
                  <div style={styles.donutWrap} data-testid="cluster-donut">
                    <Donut data={stats.top_clusters} />
                    <div style={styles.legend}>
                      {stats.top_clusters.map((c, i) => (
                        <div key={c.name} style={styles.legendItem}>
                          <span style={{ ...styles.legendDot, background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                          <span style={styles.legendName}>{c.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={styles.clusterRow}>
                    {stats.top_clusters.map((c) => {
                      const max = stats.top_clusters[0].count || 1;
                      return (
                        <div key={c.name} style={styles.clusterItem} data-testid={`cluster-${c.name}`}>
                          <div style={styles.clusterHead}><span>{c.name}</span><b>{c.count}</b></div>
                          <div style={styles.clusterTrack}><div style={{ ...styles.clusterFill, width: `${Math.round((c.count / max) * 100)}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {categories.map((cat) => {
          const items = artifacts.filter((a) => a.category === cat);
          const CatIcon = CARD_ICONS[cat] || FileText;
          return (
            <section key={cat} style={{ marginTop: 26 }} data-testid={`portal-cat-${cat}`}>
              <div style={styles.catHead}><CatIcon size={18} color={LIME} /><span>{cat}</span></div>
              <div style={styles.grid}>
                {items.map((it) => {
                  const Ic = iconFor(it);
                  return (
                    <div key={it.key} style={styles.card} data-testid={`portal-card-${it.key}`}>
                      {it.preview && (
                        <div style={styles.previewRow}>
                          {(it.preview_pages || [1]).map((pg) => (
                            <a
                              key={pg}
                              data-testid={`portal-preview-${it.key}-p${pg}`}
                              href={`${API}/portal/preview/${it.key}?token=${encodeURIComponent(token)}&page=${pg}`}
                              target="_blank"
                              rel="noreferrer"
                              style={styles.previewWrap}
                            >
                              <img
                                src={`${API}/portal/preview/${it.key}?token=${encodeURIComponent(token)}&page=${pg}`}
                                alt={`Anteprima ${it.title} pag. ${pg}`}
                                style={styles.previewImg}
                                loading="lazy"
                              />
                              <span style={styles.previewBadge}>{pg === 1 ? "Copertina" : `Pag. ${pg}`}</span>
                            </a>
                          ))}
                        </div>
                      )}
                      <div style={styles.cardTop}>
                        <div style={styles.cardIcon}><Ic size={22} color={NAVY} /></div>
                        <div style={styles.cardTextWrap}>
                          <div style={styles.cardTitle}>{it.title}</div>
                          <div style={styles.cardDesc}>{it.desc}</div>
                          <div style={styles.cardMeta}>{it.filename} · {it.size} · agg. {it.updated}</div>
                        </div>
                      </div>
                      {previews[it.key] && (
                        <div style={styles.xlsxPrev} data-testid={`xlsx-preview-${it.key}`}>
                          {previews[it.key].sheets && previews[it.key].sheets.length > 1 && (
                            <div style={styles.sheetTabs}>
                              {previews[it.key].sheets.map((sn) => (
                                <button key={sn} data-testid={`sheet-tab-${it.key}-${sn}`}
                                  onClick={() => fetchXlsx(it.key, sn)}
                                  style={{ ...styles.sheetTab, ...(previews[it.key].sheet === sn ? styles.sheetTabActive : {}) }}>
                                  {sn}
                                </button>
                              ))}
                            </div>
                          )}
                          <div style={styles.xlsxScroll}>
                            <table style={styles.xlsxTable}>
                              <thead><tr>{(previews[it.key].header || []).map((h, i) => (<th key={i} style={styles.xlsxTh}>{h}</th>))}</tr></thead>
                              <tbody>{(previews[it.key].rows || []).map((r, ri) => (<tr key={ri}>{r.map((c, ci) => (<td key={ci} style={styles.xlsxTd}>{c}</td>))}</tr>))}</tbody>
                            </table>
                          </div>
                          <div style={styles.xlsxNote}>Foglio "{previews[it.key].sheet}" · anteprima prime righe</div>
                        </div>
                      )}
                      <div style={styles.actions}>
                        {it.filename.endsWith(".xlsx") && (
                          <button data-testid={`portal-peek-${it.key}`} style={styles.peekBtn} onClick={() => toggleXlsx(it.key)}>
                            <Table size={14} /> {previews[it.key] ? "Nascondi anteprima" : "Anteprima dati"}
                          </button>
                        )}
                        <a
                          data-testid={`portal-download-${it.key}`}
                          href={`${API}/portal/download/${it.key}?token=${encodeURIComponent(token)}`}
                          style={styles.dlBtn}
                        >
                          <Download size={15} /> Scarica
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        {artifacts.length === 0 && (
          <div style={styles.empty}>Nessun file disponibile al momento. Le elaborazioni potrebbero essere ancora in corso.</div>
        )}
      </main>
      <footer style={styles.footer}>TELCAVOIP INTERNATIONAL · contenuti riservati e confidenziali · uso interno SOSTRAVEL.COM</footer>

      {showInvite && (
        <div style={styles.modalOverlay} data-testid="invite-modal" onClick={() => setShowInvite(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <button data-testid="invite-close-btn" style={styles.modalClose} onClick={() => setShowInvite(false)}><X size={18} /></button>
            <div style={styles.limeBar} />
            <h2 style={styles.modalTitle}>Invia istruzioni di accesso</h2>
            <p style={styles.modalSub}>Invieremo un'email brandizzata TELCAVOIP con il link al portale e i passaggi per accedere. Puoi indicare più destinatari (uno per riga o separati da virgola). Sono ammessi solo indirizzi dei domini autorizzati.</p>
            <label style={styles.modalLabel}>Destinatari</label>
            <textarea
              data-testid="invite-emails-input"
              style={styles.modalTextarea}
              rows={3}
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              placeholder="maura.pisati@sostravel.com, nome@telcavoip.ai"
            />
            {inviteMsg && (
              <div data-testid="invite-msg" style={{ ...styles.modalMsg, ...(inviteMsg.ok ? styles.modalMsgOk : styles.modalMsgErr) }}>{inviteMsg.text}</div>
            )}
            <div style={styles.modalActions}>
              <button data-testid="invite-cancel-btn" style={styles.modalCancel} onClick={() => setShowInvite(false)}>Annulla</button>
              <button data-testid="invite-send-btn" style={styles.modalSend} disabled={inviteLoading || !inviteEmails.trim()} onClick={sendInvite}>
                <Send size={15} /> {inviteLoading ? "Invio…" : "Invia email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#F4F6FA", fontFamily: "Helvetica, Arial, sans-serif", color: "#161A2B" },
  header: { background: NAVY, boxShadow: "0 2px 12px rgba(0,0,0,0.15)" },
  headerInner: { maxWidth: 1080, margin: "0 auto", padding: "14px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  logoutBtn: { display: "flex", alignItems: "center", gap: 6, background: "transparent", color: "#c9d6ea", border: "1px solid #33517A", borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontSize: 12.5 },
  inviteBtn: { display: "flex", alignItems: "center", gap: 6, background: LIME, color: NAVY, border: "none", borderRadius: 8, padding: "8px 13px", cursor: "pointer", fontSize: 12.5, fontWeight: 700 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(10,10,70,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 50 },
  modalCard: { position: "relative", background: "#fff", borderRadius: 16, padding: "26px 26px 22px", width: "100%", maxWidth: 460, boxShadow: "0 20px 60px rgba(0,0,0,0.35)" },
  modalClose: { position: "absolute", top: 14, right: 14, background: "transparent", border: "none", cursor: "pointer", color: "#5B647B" },
  modalTitle: { fontSize: 18, fontWeight: 800, color: NAVY, margin: "8px 0 6px" },
  modalSub: { color: "#5B647B", fontSize: 12.5, lineHeight: 1.5, margin: "0 0 16px" },
  modalLabel: { display: "block", fontSize: 11.5, fontWeight: 700, color: NAVY, marginBottom: 6 },
  modalTextarea: { width: "100%", boxSizing: "border-box", border: "1.5px solid #d3dceb", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: NAVY, outline: "none", resize: "vertical", fontFamily: "Helvetica, Arial, sans-serif" },
  modalMsg: { marginTop: 12, padding: "9px 12px", borderRadius: 8, fontSize: 12 },
  modalMsgOk: { background: "#eef7ee", border: "1px solid #cde6cd", color: "#2f6b2f" },
  modalMsgErr: { background: "#fdeded", border: "1px solid #f2c4c4", color: "#b23333" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 },
  modalCancel: { background: "transparent", border: "1px solid #d3dceb", color: "#5B647B", borderRadius: 9, padding: "10px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  modalSend: { display: "inline-flex", alignItems: "center", gap: 6, background: NAVY, color: "#fff", border: "none", borderRadius: 9, padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  main: { maxWidth: 1080, margin: "0 auto", padding: "30px 22px 60px" },
  limeBar: { width: 46, height: 4, background: LIME, borderRadius: 2, marginBottom: 12 },
  h1: { fontSize: 26, fontWeight: 800, color: NAVY, margin: "0 0 6px" },
  lead: { color: "#5B647B", fontSize: 14, maxWidth: 720, lineHeight: 1.5 },
  catHead: { display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: NAVY, fontSize: 15, marginBottom: 12, paddingBottom: 6, borderBottom: `2px solid #E3EAF2` },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16, alignItems: "stretch" },
  card: { background: "#fff", border: "1px solid #E3EAF2", borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 1px 4px rgba(10,10,70,0.04)", height: "100%", boxSizing: "border-box" },
  cardTop: { display: "flex", gap: 14, alignItems: "flex-start" },
  previewRow: { display: "flex", gap: 8 },
  previewWrap: { position: "relative", display: "block", flex: 1, minWidth: 0, borderRadius: 10, overflow: "hidden", border: "1px solid #E3EAF2", background: "#F4F6FA", lineHeight: 0 },
  previewImg: { width: "100%", height: 190, objectFit: "cover", objectPosition: "top", display: "block", transition: "transform 0.25s ease" },
  previewBadge: { position: "absolute", top: 8, right: 8, background: "rgba(10,10,70,0.82)", color: "#fff", fontSize: 10.5, fontWeight: 600, padding: "4px 8px", borderRadius: 6, letterSpacing: 0.3 },
  cardTextWrap: { flex: 1, minWidth: 0 },
  cardIcon: { width: 44, height: 44, borderRadius: 10, background: "#EEF3FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  cardTitle: { fontWeight: 700, color: NAVY, fontSize: 14.5, marginBottom: 4, lineHeight: 1.3 },
  cardDesc: { color: "#5B647B", fontSize: 12.5, lineHeight: 1.45, marginBottom: 8 },
  cardMeta: { color: "#9aa4b5", fontSize: 11, wordBreak: "break-word" },
  dlBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, background: NAVY, color: "#fff", textDecoration: "none", padding: "10px 14px", borderRadius: 9, fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" },
  actions: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: "auto", alignItems: "center" },
  peekBtn: { display: "inline-flex", alignItems: "center", gap: 6, background: "#EEF3FF", color: NAVY, border: "1px solid #d3dceb", borderRadius: 9, padding: "9px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  xlsxPrev: { border: "1px solid #E3EAF2", borderRadius: 10, overflow: "hidden", background: "#FAFBFE" },
  sheetTabs: { display: "flex", gap: 4, flexWrap: "wrap", padding: 6, background: "#EEF3FF", borderBottom: "1px solid #E3EAF2" },
  sheetTab: { background: "#fff", border: "1px solid #d3dceb", borderRadius: 7, padding: "4px 8px", fontSize: 10.5, color: "#5B647B", cursor: "pointer", fontWeight: 600 },
  sheetTabActive: { background: NAVY, color: "#fff", border: `1px solid ${NAVY}` },
  xlsxScroll: { overflowX: "auto" },
  xlsxTable: { borderCollapse: "collapse", width: "100%", fontSize: 11 },
  xlsxTh: { background: NAVY, color: "#fff", textAlign: "left", padding: "6px 8px", fontWeight: 600, whiteSpace: "nowrap" },
  xlsxTd: { borderTop: "1px solid #E3EAF2", padding: "5px 8px", color: "#3a4258", whiteSpace: "nowrap" },
  xlsxNote: { color: "#9aa4b5", fontSize: 10.5, padding: "6px 8px" },
  dashWrap: { marginTop: 22, background: "#fff", border: "1px solid #E3EAF2", borderRadius: 16, padding: 18, boxShadow: "0 1px 4px rgba(10,10,70,0.04)" },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 },
  statCard: { background: "#0A0A46", borderRadius: 12, padding: "16px 18px", color: "#fff" },
  statValue: { fontSize: 28, fontWeight: 800, lineHeight: 1.1, letterSpacing: 0.3 },
  statLabel: { fontSize: 12.5, color: "#c9d6ea", marginTop: 4, fontWeight: 600 },
  statSub: { fontSize: 11, color: "#8FB000", marginTop: 3, fontWeight: 600 },
  clusterBox: { marginTop: 16, paddingTop: 14, borderTop: "1px solid #E3EAF2" },
  clusterTitle: { fontWeight: 700, color: NAVY, fontSize: 13.5, marginBottom: 12 },
  clusterLayout: { display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" },
  donutWrap: { display: "flex", gap: 14, alignItems: "center", flexShrink: 0 },
  legend: { display: "flex", flexDirection: "column", gap: 5 },
  legendItem: { display: "flex", alignItems: "center", gap: 7 },
  legendDot: { width: 10, height: 10, borderRadius: 3, flexShrink: 0 },
  legendName: { fontSize: 11.5, color: "#3a4258" },
  clusterRow: { flex: 1, minWidth: 260, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, alignContent: "start" },
  clusterItem: {},
  clusterHead: { display: "flex", justifyContent: "space-between", fontSize: 12, color: "#3a4258", marginBottom: 5 },
  clusterTrack: { height: 8, background: "#EEF3FF", borderRadius: 999, overflow: "hidden" },
  clusterFill: { height: "100%", background: LIME, borderRadius: 999 },
  expiryNote: { color: "#8a94a8", fontSize: 10.5, marginTop: 8 },
  warnBanner: { display: "flex", alignItems: "center", gap: 8, background: "#FFF6E6", border: "1px solid #F0D08A", color: "#7a5b12", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, marginBottom: 16 },
  warnDot: { width: 9, height: 9, borderRadius: 999, background: "#E8A33D", flexShrink: 0 },
  empty: { marginTop: 30, padding: 24, background: "#fff", borderRadius: 12, color: "#5B647B", textAlign: "center", border: "1px dashed #cfd8e6" },
  footer: { textAlign: "center", color: "#8a94a8", fontSize: 11.5, padding: "20px" },
  // login
  loginWrap: { minHeight: "100vh", background: `url('/telcavoip_bg.jpeg') center/cover no-repeat, ${NAVY}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", fontFamily: "Helvetica, Arial, sans-serif", padding: 20 },
  loginOverlay: { position: "absolute", inset: 0, background: "rgba(10,10,70,0.55)" },
  loginCard: { position: "relative", background: "#fff", borderRadius: 18, padding: "34px 34px 26px", width: "100%", maxWidth: 420, textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" },
  intl: { letterSpacing: 4, fontSize: 9, color: "#5B647B", marginBottom: 10 },
  loginTitle: { fontSize: 20, fontWeight: 800, color: NAVY, margin: "6px 0" },
  loginSub: { color: "#5B647B", fontSize: 13, lineHeight: 1.5, margin: "0 0 18px" },
  inputRow: { display: "flex", alignItems: "center", gap: 8, border: "1.5px solid #d3dceb", borderRadius: 10, padding: "10px 12px", marginBottom: 12 },
  input: { border: "none", outline: "none", flex: 1, fontSize: 14, color: NAVY },
  primaryBtn: { width: "100%", background: NAVY, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  linkBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, background: "transparent", color: "#5B647B", border: "none", cursor: "pointer", fontSize: 12.5, marginTop: 10 },
  error: { color: "#D64545", fontSize: 12.5, marginBottom: 10 },
  infoBox: { color: "#2f6b2f", background: "#eef7ee", border: "1px solid #cde6cd", borderRadius: 8, fontSize: 12, padding: "8px 10px", marginBottom: 12 },
  confidential: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: "#8a94a8", fontSize: 11, marginTop: 16 },
  stepDots: { display: "flex", justifyContent: "center", gap: 6, marginTop: 16 },
  dot: { width: 7, height: 7, borderRadius: 999, background: "#d3dceb" },
  dotActive: { width: 7, height: 7, borderRadius: 999, background: NAVY },
  dotDone: { width: 7, height: 7, borderRadius: 999, background: LIME },
};
